import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdAdd, MdOutlineDelete } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { useAppSelector } from '../../redux';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { companiesSelector } from '../../redux/user';
import {
  addAiBlacklistCid,
  fetchAiBlacklist,
  removeAiBlacklistCid
} from './client-data-mapping-api';

export const AI_BLACKLIST_TAB_KEY = 'ai_blacklist';

interface BlacklistRow {
  cid: string;
}

// Hoisted out of the component so BPMTable doesn't see a new component type on every render
// (react/no-unstable-nested-components).
const NoBlacklistRows = () => (
  <span>No companies are currently excluded from AI/agent access.</span>
);

// Global-admin-only tab for company_ai_blacklist -- the list of companies whose data the
// AI/agent tool surface (Tinuiti MCP) must never return. Add via a company dropdown, remove
// with a row-level trash button (confirmed via AreYouSure). Backed by the users-service
// /aiBlacklist endpoints.
export const AiBlacklistTab = () => {
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const companies = useAppSelector(companiesSelector);

  const [cids, setCids] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [pendingCid, setPendingCid] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await fetchAiBlacklist();
      setCids(next.slice().sort());
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      // Set unconditionally (not just on success) so a failed initial fetch still resolves the
      // spinner to an empty/stale table rather than leaving it stuck once the error modal closes.
      setLoaded(true);
    }
  }, [setError]);

  useEffect(() => {
    load();
  }, [load]);

  const addableOptions = useMemo(
    () =>
      companies
        .map(c => c.company)
        .filter(cid => !cids.includes(cid))
        .sort(),
    [companies, cids]
  );

  const handleAdd = async () => {
    if (!pendingCid || saving) return;
    setSaving(true);
    try {
      const next = await addAiBlacklistCid(pendingCid);
      setCids(next.slice().sort());
      setPendingCid(null);
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = useCallback(
    async (cid: string) => {
      try {
        await setAreYouSure({
          title: 'Remove from AI blacklist',
          message: `Allow AI/agent tools to return data for "${cid}" again?`,
          okayText: 'Remove'
        });
      } catch {
        return; // user cancelled
      }
      setSaving(true);
      try {
        const next = await removeAiBlacklistCid(cid);
        setCids(next.slice().sort());
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setSaving(false);
      }
    },
    [setAreYouSure, setError]
  );

  const headers: TypedHeader<BlacklistRow>[] = useMemo(
    () => [
      { label: 'Company', field: 'cid', flex: 1 },
      {
        label: '',
        name: 'remove',
        width: 48,
        nonFilterable: true,
        renderer: row => (
          <IconButton
            appearance={Appearance.EMPTY}
            tooltip={{ text: 'Remove from blacklist' }}
            disabled={saving}
            onClick={() => handleRemove(row.cid)}
          >
            <MdOutlineDelete />
          </IconButton>
        )
      }
    ],
    [saving, handleRemove]
  );

  const rows: BlacklistRow[] = useMemo(() => cids.map(cid => ({ cid })), [cids]);

  return (
    <div data-testid='ai-blacklist-tab' className='client-data-mapping-page is-global'>
      {!loaded && (
        <div className='client-data-mapping-loading'>
          <Spinner />
        </div>
      )}
      {loaded && (
        <>
          <div className='client-data-mapping-toolbar ai-blacklist-toolbar'>
            <Dropdown
              size={Size.LG}
              searchable
              options={addableOptions}
              value={pendingCid}
              onChange={setPendingCid}
              placeholder='Select a client to exclude'
            />
            <Button
              size={Size.LG}
              data-testid='add-ai-blacklist-button'
              leadingElement={<MdAdd />}
              disabled={!pendingCid || saving}
              onClick={handleAdd}
            >
              Add to blacklist
            </Button>
          </div>
          <div className='client-data-mapping-table'>
            <BPMTable<BlacklistRow>
              className='mapping-table'
              headers={headers}
              data={rows}
              noRowsRenderer={NoBlacklistRows}
            />
          </div>
        </>
      )}
    </div>
  );
};
